import React, { useState, useEffect, useCallback } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import './ToastNotification.css';

/**
 * Global toast notification system.
 *
 * Usage from anywhere:
 *   window.dispatchEvent(new CustomEvent('app:toast', {
 *     detail: { message: 'Médium bylo vytvořeno.', variant: 'success' }
 *   }));
 *
 * Supported variants: success, danger, warning, info
 */
function ToastNotification() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, variant = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, variant }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const { message, variant } = e.detail || {};
      if (message) {
        addToast(message, variant || 'success');
      }
    };
    window.addEventListener('app:toast', handler);
    return () => window.removeEventListener('app:toast', handler);
  }, [addToast]);

  const iconMap = {
    success: 'bi-check-circle-fill',
    danger: 'bi-exclamation-triangle-fill',
    warning: 'bi-exclamation-circle-fill',
    info: 'bi-info-circle-fill',
  };

  return (
    <ToastContainer position="top-end" className="toast-notification-container">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          onClose={() => removeToast(toast.id)}
          autohide
          delay={4000}
          className={`toast-notification toast-${toast.variant}`}
        >
          <Toast.Body>
            <i className={`bi ${iconMap[toast.variant] || iconMap.success} me-2`}></i>
            {toast.message}
          </Toast.Body>
        </Toast>
      ))}
    </ToastContainer>
  );
}

/**
 * Helper to fire a toast from anywhere (no import of React context needed).
 */
export function showToast(message, variant = 'success') {
  window.dispatchEvent(new CustomEvent('app:toast', {
    detail: { message, variant },
  }));
}

export default ToastNotification;
